import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { parsedFiles } from '@/mocks/homeData';

const statusTone: Record<string, string> = {
  已解析: 'bg-primary-400 text-foreground-950',
  波形比对中: 'bg-secondary-400 text-foreground-950',
  关键帧提取: 'bg-accent-400 text-foreground-950',
  待复核: 'bg-background-300 text-foreground-900',
};

export default function ParseSection() {
  const [activeId, setActiveId] = useState(parsedFiles[0].id);
  const active = parsedFiles.find((file) => file.id === activeId) ?? parsedFiles[0];

  return (
    <section id="parse" className="w-full bg-background-50 py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-10">
        <SectionHeading
          eyebrow="File Parse Station"
          title="文件解析站：把证据拆开看，别只信一张压缩图"
          description="上传的合同、对账单、录音与录屏会先算一次哈希再解析结构，修改痕迹、时间戳缺口与异常段落会被单独列出来，原始文件保持不动。"
        />

        <div className="mt-10 rounded-lg overflow-hidden border border-primary-800 bg-primary-900">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-4 border-b border-primary-700/60">
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-accent-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-secondary-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-primary-400" />
              <span className="ml-2 text-xs md:text-sm text-background-100/80 tracking-wide">
                解析队列 · 5 个任务
              </span>
            </div>
            <span className="text-xs text-primary-100/70 flex items-center gap-2">
              <i className="ri-shield-check-line" />
              哈希校验已开启
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-5 border-b lg:border-b-0 lg:border-r border-primary-700/60 p-4 md:p-6">
              <div className="flex flex-col gap-2">
                {parsedFiles.map((file) => {
                  const isActive = file.id === activeId;
                  return (
                    <button
                      key={file.id}
                      type="button"
                      onClick={() => setActiveId(file.id)}
                      className={`w-full text-left rounded-md px-3 py-3 transition-colors cursor-pointer border ${
                        isActive
                          ? 'bg-background-50/10 border-primary-500'
                          : 'border-transparent hover:bg-background-50/5'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs md:text-sm text-background-50 truncate">
                          {file.name}
                        </span>
                        <span className="text-[10px] md:text-xs text-primary-200 shrink-0">
                          {file.kind}
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 w-full rounded-full bg-primary-800 overflow-hidden">
                        <span
                          className="block h-full rounded-full bg-accent-400"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-7 p-4 md:p-6">
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={`px-2.5 py-1 rounded-md text-xs whitespace-nowrap ${
                    statusTone[active.status] ?? 'bg-primary-400 text-foreground-950'
                  }`}
                >
                  {active.status}
                </span>
                <span className="text-xs text-primary-100/70 flex items-center gap-1">
                  <i className="ri-file-copy-2-line" />
                  原始文件未修改
                </span>
              </div>

              <h3 className="mt-4 text-lg md:text-xl font-semibold text-background-50 break-all">
                {active.name}
              </h3>

              <dl className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="rounded-md bg-primary-950/50 px-3 py-3">
                  <dt className="text-xs text-primary-200/70">文件类型</dt>
                  <dd className="mt-1 text-sm text-background-50">{active.kind}</dd>
                </div>
                <div className="rounded-md bg-primary-950/50 px-3 py-3">
                  <dt className="text-xs text-primary-200/70">文件体积</dt>
                  <dd className="mt-1 text-sm text-background-50">{active.size}</dd>
                </div>
                <div className="rounded-md bg-primary-950/50 px-3 py-3 col-span-2 md:col-span-1">
                  <dt className="text-xs text-primary-200/70">文件哈希</dt>
                  <dd className="mt-1 text-sm text-background-50 break-all">{active.hash}</dd>
                </div>
              </dl>

              <div className="mt-6 rounded-md border border-primary-700/60 p-4">
                <p className="text-xs tracking-[0.2em] text-primary-200/70">解析进度</p>
                <div className="mt-3 h-2 w-full rounded-full bg-primary-950/60 overflow-hidden">
                  <span
                    className="block h-full rounded-full bg-accent-500 transition-all duration-500"
                    style={{ width: `${active.progress}%` }}
                  />
                </div>
                <p className="mt-3 text-xs text-primary-100/70">
                  当前任务进度 {active.progress}% · 解析完成后会自动挂到对应的时间线锤节点上
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-2-line" />
                  下载解析报告
                </button>
                <button
                  type="button"
                  className="px-5 py-2.5 rounded-md border border-primary-600 text-primary-100 text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-search-line" />
                  查看修改痕迹
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}